import { useEffect, useState } from "react";
import { RotateCcw, Ticket } from "lucide-react";
import { generateTicket, resetDailyQueue, subscribeToWaitingCount, QueueTicket } from "../../../services/queueService";
import { showToast } from "../../../utils/toast";
import ConfirmacaoModal from "./ConfirmacaoModal";

const printTicket = (type: "normal" | "preferential", number: string) => {
  const senha = `${type === "preferential" ? "R" : "E"}-${number}`;

  document.getElementById("__print_style__")?.remove();
  document.getElementById("__print_ticket__")?.remove();

  const style = document.createElement("style");
  style.id = "__print_style__";
  style.textContent = `
    @media screen { #__print_ticket__ { display: none !important; } }
    @media print {
      body > *:not(#__print_ticket__) { display: none !important; }
      #__print_ticket__ {
        display: block !important;
        font-family: 'Courier New', Courier, monospace;
        text-align: center;
        width: 74mm;
        padding: 4mm 2mm;
      }
      @page { size: 80mm auto; margin: 0; }
      .pt-header { font-size: 20pt; font-weight: bold; margin-bottom: 4mm; letter-spacing: 1.5pt; }
      .pt-linha  { border-top: 1.5pt dashed #000; margin: 4mm 0; }
      .pt-tipo   { font-size: 18pt; font-weight: bold; margin-bottom: 3mm; }
      .pt-senha  { font-size: 52pt; font-weight: bold; margin: 6mm 0; line-height: 1; }
      .pt-footer { font-size: 14pt; font-weight: bold; margin-top: 4mm; }
      .pt-data   { font-size: 11pt; margin-top: 3mm; }
      .pt-spacer { height: 30mm; }
    }
  `;
  document.head.appendChild(style);

  const div = document.createElement("div");
  div.id = "__print_ticket__";
  div.innerHTML = `
    <div class="pt-header">ATENDIMENTO</div>
    <div class="pt-linha"></div>
    <div class="pt-tipo">${type === "preferential" ? "RETIRADA" : "ESCOLHER"}</div>
    <div class="pt-senha">${senha}</div>
    <div class="pt-linha"></div>
    <div class="pt-footer">Aguarde ser chamado</div>
    <div class="pt-data">${new Date().toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</div>
    <div class="pt-spacer"></div>
  `;
  document.body.appendChild(div);
  window.print();
  setTimeout(() => { style.remove(); div.remove(); }, 1000);
};

interface Props {
  userId: string;
}

const GerarSenha = ({ userId }: Props) => {
  const [loading, setLoading] = useState<"normal" | "preferential" | null>(null);
  const [resetting, setResetting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [last, setLast] = useState<QueueTicket | null>(null);
  const [totalEspera, setTotalEspera] = useState(0);

  useEffect(() => {
    if (!userId) return;
    const [normal, preferential] = [{ count: 0 }, { count: 0 }];
    const update = () => setTotalEspera(normal.count + preferential.count);

    const unsubs = [
      subscribeToWaitingCount("normal", userId, (n) => { normal.count = n; update(); }),
      subscribeToWaitingCount("preferential", userId, (n) => { preferential.count = n; update(); }),
    ];
    return () => unsubs.forEach((fn) => fn());
  }, [userId]);

  const handle = async (type: "normal" | "preferential") => {
    try {
      setLoading(type);
      const ticket = await generateTicket(type, userId);
      setLast(ticket);
      showToast.success(`Senha gerada: ${type === "preferential" ? "R" : "E"}-${ticket.number}`);
      printTicket(type, ticket.number);
    } catch {
      showToast.error("Erro ao gerar senha");
    } finally {
      setLoading(null);
    }
  };

  const handleReset = async () => {
    try {
      setResetting(true);
      await resetDailyQueue(userId);
      setLast(null);
      setShowConfirm(false);
      showToast.success("Fila resetada — próxima senha será 001");
    } catch {
      showToast.error("Erro ao resetar fila");
    } finally {
      setResetting(false);
    }
  };

  const busy = loading !== null || resetting;

  return (
    <div className="painel-card">
      <div className="painel-card-header">
        <div className="painel-card-icon"><Ticket size={20} /></div>
        <div>
          <p className="painel-card-title">Gerar Senha</p>
          <p className="painel-card-subtitle">Emite e imprime o ticket</p>
        </div>
      </div>

      <div className="painel-actions">
        <button
          className="painel-btn painel-btn-primary"
          onClick={() => handle("normal")}
          disabled={busy}
        >
          {loading === "normal" ? "Gerando…" : "Escolher"}
        </button>
        <button
          className="painel-btn painel-btn-secondary"
          onClick={() => handle("preferential")}
          disabled={busy}
        >
          {loading === "preferential" ? "Gerando…" : "Retirada"}
        </button>
      </div>

      {last && (
        <div className="painel-result">
          <span>Última emitida</span>
          <strong>{last.type === "preferential" ? "R" : "E"}-{last.number}</strong>
        </div>
      )}

      {(totalEspera > 0 || last !== null) && (
        <div style={{ marginTop: "12px", borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: "12px" }}>
          <button
            className="painel-btn painel-btn-ghost"
            onClick={() => setShowConfirm(true)}
            disabled={busy}
            style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", color: "#dc2626", borderColor: "#dc2626" }}
          >
            <RotateCcw size={14} />
            Resetar Fila (voltar ao 001)
          </button>
        </div>
      )}

      <ConfirmacaoModal
        show={showConfirm}
        titulo="Resetar fila de senhas?"
        descricao="Todas as senhas de hoje serão apagadas e a contagem volta ao 001. Esta ação não pode ser desfeita."
        textoBotaoConfirmar="Sim, resetar"
        onConfirmar={handleReset}
        onCancelar={() => setShowConfirm(false)}
        loading={resetting}
      />
    </div>
  );
};

export default GerarSenha;
